import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// MICRO-PROMPT 3 — Public-facing landing page for Good Friar Music Publishing.
// Route: /gfmp (premium publishing & sync identity only).
//
// Strict scope:
//   - No Kandake exposure. No auth, no portal, no compliance records.
//   - No public catalogue. No intake form, payments, uploads, or onboarding.
//   - Logo placed AS-IS on a deep near-black canvas so the gold ornament
//     reads with full clarity. Transparent PNG, proportions preserved.
//   - Visual family: Durban 49 Group (cream / charcoal / burgundy / gold)
//     but with a darker, more cinematic register than /d49.

// --- design tokens (page-local, drawn from the global GFMP-Kandake palette) ---
const INK     = "hsl(20 14% 6%)";         // deep near-black canvas
const INK_2   = "hsl(20 12% 9%)";         // slightly raised black panel
const CREAM   = "hsl(36 33% 96%)";        // warm cream
const CREAM_2 = "hsl(36 22% 93%)";        // muted cream
const BURG    = "hsl(347 60% 22%)";       // deep burgundy
const GOLD    = "hsl(40 48% 58%)";        // restrained gold
const CHAR    = "hsl(20 10% 16%)";        // charcoal

export default function GFMP() {
  return (
    <div
      data-testid="gfmp-page"
      className="min-h-screen w-full flex flex-col"
      style={{ background: INK, color: CREAM }}
    >
      {/* ============================================================
          1) HERO — gold crest on deep near-black, cinematic and calm
          ============================================================ */}
      <section
        data-testid="gfmp-hero"
        className="relative w-full overflow-hidden"
        style={{
          // Layered radial accents add depth without busy texture.
          background:
            "radial-gradient(1100px 600px at 50% 0%, hsl(347 60% 22% / 0.28), transparent 60%)," +
            "radial-gradient(900px 500px at 100% 100%, hsl(40 48% 58% / 0.10), transparent 60%)," +
            INK,
        }}
      >
        {/* Restrained editorial header */}
        <header className="max-w-6xl w-full mx-auto px-6 sm:px-10 pt-6 sm:pt-8 flex items-center justify-between">
          <div
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em]"
            style={{ color: `${GOLD}` }}
            data-testid="gfmp-eyebrow"
          >
            <span
              aria-hidden="true"
              style={{ width: 18, height: 1, background: `${GOLD}` }}
            />
            Good Friar Music Publishing
          </div>
          <div
            className="text-[10px] uppercase tracking-[0.24em] hidden sm:block"
            style={{ color: `${CREAM}99` }}
          >
            A Durban 49 Group Company
          </div>
        </header>

        {/* Hero body */}
        <div className="max-w-5xl w-full mx-auto px-6 sm:px-10 pt-10 sm:pt-16 pb-20 sm:pb-28 flex flex-col items-center text-center">
          {/* Logo is the hero — pushed substantially larger so the
              gold crest is the unmistakable focal mark of the page.
              The image already contains the "GOOD FRIAR MUSIC PUBLISHING"
              wordmark above the crest, so we do NOT repeat the company
              name as text. Container has no fill, no recolour, no crop. */}
          <img
            src="/brand/gfmp-crest.png"
            alt="Good Friar Music Publishing"
            draggable="false"
            data-testid="gfmp-logo"
            className="block w-[340px] sm:w-[500px] md:w-[640px] lg:w-[720px] h-auto select-none"
          />

          <h1
            className="font-display mt-8 sm:mt-10 text-[26px] sm:text-[34px] md:text-[40px] leading-[1.12] tracking-tight max-w-2xl font-medium"
            data-testid="gfmp-headline"
            style={{ color: `${CREAM}e6` }}
          >
            Music Built for Culture.{" "}
            <span style={{ color: GOLD }}>Cleared for Opportunity.</span>
          </h1>

          <p
            className="mt-5 sm:mt-6 text-[13.5px] sm:text-[15px] leading-relaxed max-w-xl"
            style={{ color: `${CREAM}b8` }}
            data-testid="gfmp-body"
          >
            Good Friar Music Publishing represents distinctive music for film,
            television, advertising, games, global brands, and culture.
          </p>

          <p
            className="mt-2.5 text-[12.5px] sm:text-[13.5px] max-w-xl"
            style={{ color: `${CREAM}8c` }}
            data-testid="gfmp-secondary"
          >
            Explore the catalogue, find music for your brief, and connect with
            the GFMP Sync Desk.
          </p>

          {/* Sync Catalogue — live link to the public catalogue surface. */}
          <Link
            to="/gfmp/sync-catalogue"
            className="mt-10 sm:mt-12 inline-flex items-center gap-3 px-5 py-2.5 rounded-full transition-opacity hover:opacity-90"
            data-testid="gfmp-catalogue-link"
            style={{
              background: "transparent",
              border: `1px solid ${GOLD}66`,
              color: `${CREAM}f0`,
            }}
            aria-label="Open the GFMP Sync Catalogue"
          >
            <span
              aria-hidden="true"
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: GOLD, boxShadow: `0 0 0 4px ${GOLD}22` }}
            />
            <span className="text-[11px] sm:text-[12px] uppercase tracking-[0.22em]">
              Enter the Sync Catalogue
            </span>
          </Link>

          {/* Hairline accent rule */}
          <div
            className="mt-16 sm:mt-20 w-16 h-px"
            aria-hidden="true"
            style={{ background: `${GOLD}80` }}
          />
        </div>
      </section>

      {/* ============================================================
          2) POSITIONING — cream panel, editorial typography
          ============================================================ */}
      <section
        data-testid="gfmp-positioning"
        className="w-full"
        style={{ background: CREAM, color: INK }}
      >
        <div className="max-w-5xl w-full mx-auto px-6 sm:px-10 py-20 sm:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-start">
            <div className="md:col-span-5">
              <div
                className="text-[10px] uppercase tracking-[0.24em] mb-5"
                style={{ color: BURG }}
                data-testid="gfmp-positioning-eyebrow"
              >
                Positioning
              </div>
              <h2
                className="font-display text-[32px] sm:text-[44px] md:text-[52px] leading-[1.05] tracking-tight"
                data-testid="gfmp-positioning-heading"
              >
                Music With a{" "}
                <span style={{ color: BURG }}>Place to Go.</span>
              </h2>
            </div>
            <div className="md:col-span-7">
              <p
                className="text-base sm:text-lg leading-relaxed"
                style={{ color: `${INK}cc` }}
                data-testid="gfmp-positioning-body"
              >
                From cinematic storytelling to global rhythm, contemporary soul,
                reggae, jazz fusion, faith-driven music, and beyond, GFMP is
                built to connect distinctive music with meaningful opportunities.
              </p>
              <div
                className="mt-10 w-12 h-px"
                aria-hidden="true"
                style={{ background: `${GOLD}` }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          3) SYNC-FOCUSED — charcoal panel with three-point process
          ============================================================ */}
      <section
        data-testid="gfmp-sync-focused"
        className="w-full"
        style={{ background: CHAR, color: CREAM }}
      >
        <div className="max-w-5xl w-full mx-auto px-6 sm:px-10 py-20 sm:py-28">
          <div className="max-w-3xl">
            <div
              className="text-[10px] uppercase tracking-[0.24em] mb-5"
              style={{ color: GOLD }}
              data-testid="gfmp-sync-eyebrow"
            >
              For the Music Supervisor
            </div>
            <h2
              className="font-display text-[32px] sm:text-[44px] md:text-[52px] leading-[1.05] tracking-tight"
              data-testid="gfmp-sync-heading"
            >
              For Briefs That Need{" "}
              <span style={{ color: GOLD }}>More Than Background Music.</span>
            </h2>
          </div>

          {/* Three-step refined list */}
          <ol
            className="mt-14 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10"
            data-testid="gfmp-sync-points"
          >
            {[
              { label: "Hear it",                   key: "hear-it" },
              { label: "Match it to the brief",     key: "match-it" },
              { label: "Confirm rights readiness",  key: "confirm-rights" },
            ].map((item, i) => (
              <li
                key={item.key}
                data-testid={`gfmp-sync-point-${item.key}`}
                className="relative pl-0"
              >
                <div className="flex items-baseline gap-4">
                  <span
                    className="font-display text-[28px] sm:text-[32px] leading-none"
                    style={{ color: GOLD }}
                    aria-hidden="true"
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span
                    className="block flex-1 h-px translate-y-[-6px]"
                    aria-hidden="true"
                    style={{ background: `${GOLD}55` }}
                  />
                </div>
                <div
                  className="mt-4 text-[19px] sm:text-[20px] font-display leading-snug"
                  style={{ color: CREAM }}
                >
                  {item.label}
                </div>
              </li>
            ))}
          </ol>

          <p
            className="mt-14 sm:mt-16 text-base sm:text-lg leading-relaxed max-w-3xl"
            style={{ color: `${CREAM}b3` }}
            data-testid="gfmp-sync-supporting"
          >
            GFMP is designed to make the creative conversation faster, clearer,
            and more useful for the people placing music.
          </p>
        </div>
      </section>

      {/* ============================================================
          4) GFMP SYNC DESK — cream, contact-as-relationship (no form)
          ============================================================ */}
      <section
        data-testid="gfmp-sync-desk"
        className="w-full"
        style={{ background: CREAM_2, color: INK }}
      >
        <div className="max-w-5xl w-full mx-auto px-6 sm:px-10 py-20 sm:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-start">
            <div className="md:col-span-5">
              <div
                className="text-[10px] uppercase tracking-[0.24em] mb-5"
                style={{ color: BURG }}
                data-testid="gfmp-desk-eyebrow"
              >
                Connect
              </div>
              <h2
                className="font-display text-[32px] sm:text-[44px] md:text-[52px] leading-[1.05] tracking-tight"
                data-testid="gfmp-desk-heading"
              >
                GFMP{" "}
                <span style={{ color: BURG }}>Sync Desk</span>
              </h2>
            </div>
            <div className="md:col-span-7">
              <p
                className="text-base sm:text-lg leading-relaxed"
                style={{ color: `${INK}cc` }}
                data-testid="gfmp-desk-body"
              >
                Music supervisors, agencies, brands, producers, and creative
                partners may connect directly with the GFMP Sync Desk for
                catalogue access, brief matching, and rights-status discussions.
              </p>

              {/* Audience chips — purely informational, no actions. */}
              <ul
                className="mt-8 flex flex-wrap gap-2"
                data-testid="gfmp-desk-audience"
                aria-label="Who the Sync Desk is for"
              >
                {[
                  "Music Supervisors",
                  "Agencies",
                  "Brands",
                  "Producers",
                  "Creative Partners",
                ].map((label) => (
                  <li
                    key={label}
                    className="px-3 py-1.5 rounded-full text-[11px] tracking-wide"
                    style={{
                      background: "transparent",
                      border: `1px solid ${BURG}33`,
                      color: `${INK}cc`,
                    }}
                  >
                    {label}
                  </li>
                ))}
              </ul>

              <div
                className="mt-10 w-12 h-px"
                aria-hidden="true"
                style={{ background: BURG }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          5) FOOTER — minimal, brand line, route back to /d49
          ============================================================ */}
      <footer
        className="w-full"
        style={{
          background: INK_2,
          color: `${CREAM}99`,
          borderTop: `1px solid ${GOLD}26`,
        }}
        data-testid="gfmp-footer"
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex flex-wrap items-center justify-between gap-5">
          <div className="flex flex-col gap-0.5">
            <span
              className="font-display text-[17px] sm:text-[18px] tracking-tight"
              style={{ color: CREAM }}
              data-testid="gfmp-footer-brand"
            >
              Good Friar Music Publishing
            </span>
            <span
              className="text-[11px] uppercase tracking-[0.22em]"
              style={{ color: `${GOLD}cc` }}
              data-testid="gfmp-footer-parent"
            >
              A Durban 49 Group Company
            </span>
          </div>

          <Link
            to="/d49"
            data-testid="gfmp-back-to-d49"
            className="group inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] transition-opacity"
            style={{ color: `${CREAM}cc` }}
          >
            <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" />
            Back to The Durban 49 Group
          </Link>
        </div>

        <div
          className="max-w-6xl mx-auto px-6 sm:px-10 pb-6 text-[11px] tracking-wide flex flex-wrap items-center justify-between gap-3"
          style={{ color: `${CREAM}66` }}
        >
          <span data-testid="gfmp-footer-rights">
            © {new Date().getFullYear()} Good Friar Music Publishing. All rights reserved.
          </span>
          <span className="uppercase tracking-[0.22em]">
            Music · Sync · Rights
          </span>
        </div>
      </footer>
    </div>
  );
}
